import React, { useState } from 'react';
import { useUser } from '../../api/UserContext';
import {
  Modal,
  Form,
  Input,
  Button,
  Upload,
  Avatar,
  Row,
  Col,
  Slider,
  Tag,
  ColorPicker,
  Space,
  Divider,
  message,
  Card,
  Typography
} from 'antd';
import {
  UserOutlined,
  DeleteOutlined,
  PlusOutlined,
  UndoOutlined,
  SaveOutlined,
  UploadOutlined
} from '@ant-design/icons';

const { Text } = Typography;
const { TextArea } = Input;

const EditProfile = ({ onClose }) => {
  const { user, updateUser } = useUser();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(user.avatar || '');
  const [avatarColor, setAvatarColor] = useState(user.avatarColor || '#4a90e2');
  const [techStack, setTechStack] = useState([...(user.techStack || [])]);
  const [skills, setSkills] = useState([...(user.skills || [])]);
  const [tempSkill, setTempSkill] = useState('');
  
  // 预设颜色选项
  const colorOptions = [
    '#4a90e2', '#f39c12', '#2ecc71', '#e74c3c', 
    '#9b59b6', '#1abc9c', '#d35400', '#3498db'
  ];

  // 初始化表单值
  const initialValues = {
    name: user.name || '',
    bio: user.bio || '',
    email: user.email || '',
    position: user.position || '',
    location: user.location || '',
    joinDate: user.joinDate || '',
    socialMedia: user.socialMedia || '',
    avatar: user.avatar || ''
  };

  // 处理头像上传
  const handleUpload = (info) => {
    if (info.file.status === 'uploading') {
      return;
    }
    if (info.file.status === 'done') {
      const reader = new FileReader();
      reader.readAsDataURL(info.file.originFileObj);
      reader.onload = () => {
        setAvatarUrl(reader.result);
        message.success('头像上传成功');
      };
    } else if (info.file.status === 'error') {
      message.error('头像上传失败');
    }
  };

  // 上传前验证
  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error('只能上传 JPG/PNG 文件!');
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('图片必须小于 2MB!');
    }
    return isJpgOrPng && isLt2M;
  };

  // 添加技能标签
  const handleAddSkill = () => {
    if (tempSkill.trim() && !skills.includes(tempSkill.trim())) {
      setSkills([...skills, tempSkill.trim()]);
      setTempSkill('');
    }
  };

  // 删除技能标签
  const handleRemoveSkill = (skill) => {
    setSkills(skills.filter(s => s !== skill));
  };

  // 添加技术栈
  const handleAddTech = () => {
    setTechStack([...techStack, { name: '', level: 50 }]);
  };

  // 更新技术栈
  const handleTechChange = (index, field, value) => {
    const newTechStack = [...techStack];
    newTechStack[index][field] = value;
    setTechStack(newTechStack);
  };

  // 删除技术栈
  const handleRemoveTech = (index) => {
    setTechStack(techStack.filter((_, i) => i !== index));
  };

  // 保存表单
  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      
      const formData = {
        ...values,
        avatar: avatarUrl,
        avatarColor,
        techStack,
        skills
      };

      await updateUser(formData);
      
      message.success('个人信息更新成功');
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error('表单验证失败:', error);
      message.error('请检查表单填写是否正确');
    } finally {
      setLoading(false);
    }
  };

  // 重置表单
  const handleReset = () => {
    form.resetFields();
    setAvatarUrl(user.avatar || '');
    setAvatarColor(user.avatarColor || '#4a90e2');
    setTechStack([...(user.techStack || [])]);
    setSkills([...(user.skills || [])]);
    message.info('表单已重置');
  };

  // 清空头像
  const handleClearAvatar = () => {
    setAvatarUrl('');
  };

  // 处理取消按钮点击
  const handleCancel = (e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    onClose();
  };

  return (
    <Modal
      title="编辑个人信息"
      open={true}
      onCancel={handleCancel}
      width={800}
      footer={null}
      style={{ top: 20 }}
      // 修复: 使用 styles 替代 bodyStyle
      styles={{ 
        body: { padding: '0 24px' }
      }}
      centered
      maskClosable={true}
      keyboard={true}
    >
      <div 
        style={{ 
          maxHeight: '70vh', 
          overflowY: 'auto', 
          padding: '16px 0' 
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={initialValues}
        >
          {/* 基本信息部分 */}
          <Card
            title="基本信息"
            style={{ marginBottom: 24 }}
            size="small"
            // 修复: 使用 styles 替代 bodyStyle
            styles={{ body: { padding: 16 } }}
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Form.Item
                  label="姓名"
                  name="name"
                  rules={[{ required: true, message: '请输入姓名' }]}
                >
                  <Input placeholder="请输入您的姓名" />
                </Form.Item>
              </Col>
              
              <Col span={12}>
                <Form.Item
                  label="邮箱"
                  name="email"
                  rules={[
                    { required: true, message: '请输入邮箱' },
                    { type: 'email', message: '请输入有效的邮箱地址' }
                  ]}
                >
                  <Input placeholder="请输入邮箱地址" />
                </Form.Item>
              </Col>
              
              <Col span={12}>
                <Form.Item
                  label="职位"
                  name="position"
                >
                  <Input placeholder="请输入您的职位" />
                </Form.Item>
              </Col>
              
              <Col span={12}>
                <Form.Item
                  label="位置"
                  name="location"
                >
                  <Input placeholder="请输入您所在的城市" />
                </Form.Item>
              </Col>
              
              <Col span={12}>
                <Form.Item
                  label="加入时间"
                  name="joinDate"
                >
                  <Input placeholder="例如：2023-01-01" />
                </Form.Item>
              </Col>
              
              <Col span={12}>
                <Form.Item
                  label="社交媒体"
                  name="socialMedia"
                >
                  <Input placeholder="请输入社交媒体链接" />
                </Form.Item>
              </Col>
              
              <Col span={24}>
                <Form.Item
                  label="个人简介"
                  name="bio"
                >
                  <TextArea
                    placeholder="介绍一下自己吧"
                    rows={3}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* 头像设置部分 */}
          <Card
            title="头像设置"
            style={{ marginBottom: 24 }}
            size="small"
            // 修复: 使用 styles 替代 bodyStyle
            styles={{ body: { padding: 16 } }}
          >
            <Row gutter={[32, 32]} align="middle">
              <Col span={8}>
                <div style={{ textAlign: 'center' }}>
                  <Avatar
                    size={120}
                    src={avatarUrl}
                    icon={<UserOutlined />}
                    style={{
                      backgroundColor: avatarUrl ? 'transparent' : avatarColor,
                      fontSize: 48,
                      border: '4px solid #f0f0f0',
                      marginBottom: 16
                    }}
                  />
                  <Text type="secondary">头像预览</Text>
                </div>
              </Col>
              
              <Col span={16}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      上传头像
                    </Text>
                    <Upload
                      name="avatar"
                      showUploadList={false}
                      beforeUpload={beforeUpload}
                      onChange={handleUpload}
                      customRequest={({ onSuccess }) => {
                        setTimeout(() => onSuccess('ok'), 0);
                      }}
                    >
                      <Button icon={<UploadOutlined />}>
                        选择图片
                      </Button>
                    </Upload>
                    {avatarUrl && (
                      <Button
                        type="text"
                        danger
                        onClick={handleClearAvatar}
                        style={{ marginLeft: 8 }}
                      >
                        清除头像
                      </Button>
                    )}
                  </div>
                  
                  <Divider orientation="horizontal" />
                  
                  <div>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      或输入头像URL
                    </Text>
                    <Form.Item name="avatar">
                      <Input
                        placeholder="输入头像URL地址"
                        value={avatarUrl}
                        onChange={(e) => setAvatarUrl(e.target.value)}
                      />
                    </Form.Item>
                  </div>
                  
                  <Divider orientation="horizontal" />
                  
                  <div>
                    <Text strong style={{ display: 'block', marginBottom: 16 }}>
                      头像背景色
                    </Text>
                    <Space wrap style={{ marginBottom: 16 }}>
                      {colorOptions.map((color, index) => (
                        <button
                          key={index}
                          type="button"
                          style={{
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            backgroundColor: color,
                            border: avatarColor === color ? '2px solid #1890ff' : '2px solid transparent',
                            cursor: 'pointer',
                            padding: 0,
                            outline: 'none'
                          }}
                          onClick={() => setAvatarColor(color)}
                        />
                      ))}
                    </Space>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <Text>自定义颜色：</Text>
                      <ColorPicker
                        value={avatarColor}
                        onChange={(color) => setAvatarColor(color.toHexString())}
                      />
                      <Text code>{avatarColor}</Text>
                    </div>
                  </div>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* 技术栈部分 */}
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>技术栈</span>
                <Button
                  type="dashed"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={handleAddTech}
                >
                  添加技术
                </Button>
              </div>
            }
            style={{ marginBottom: 24 }}
            size="small"
            // 修复: 使用 styles 替代 bodyStyle
            styles={{ body: { padding: 16 } }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {techStack.map((tech, index) => (
                <Card
                  key={index}
                  size="small"
                  style={{ backgroundColor: '#fafafa' }}
                  // 修复: 使用 styles 替代 bodyStyle
                  styles={{ body: { padding: 12 } }}
                >
                  <Row gutter={[16, 16]} align="middle">
                    <Col span={8}>
                      <Input
                        placeholder="技术名称"
                        value={tech.name}
                        onChange={(e) => handleTechChange(index, 'name', e.target.value)}
                      />
                    </Col>
                    <Col span={12}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <Text type="secondary" style={{ minWidth: 60 }}>
                          熟练度：{tech.level}%
                        </Text>
                        <Slider
                          min={0}
                          max={100}
                          value={tech.level}
                          onChange={(value) => handleTechChange(index, 'level', value)}
                          style={{ flex: 1 }}
                        />
                      </div>
                    </Col>
                    <Col span={4}>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveTech(index)}
                        style={{ width: '100%' }}
                      >
                        删除
                      </Button>
                    </Col>
                  </Row>
                </Card>
              ))}
              
              {/* 添加新技术输入 */}
              <Card
                size="small"
                style={{ backgroundColor: '#f0f5ff', borderColor: '#1890ff' }}
                // 修复: 使用 styles 替代 bodyStyle
                styles={{ body: { padding: 12 } }}
              >
                <Row gutter={[16, 16]} align="middle">
                  <Col span={8}>
                    <Input
                      placeholder="输入新技能名称"
                      value={techStack[techStack.length - 1]?.name || ''}
                      onChange={(e) => {
                        if (techStack.length > 0) {
                          handleTechChange(techStack.length - 1, 'name', e.target.value);
                        }
                      }}
                    />
                  </Col>
                  <Col span={12}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <Text type="secondary" style={{ minWidth: 60 }}>
                        熟练度：{techStack[techStack.length - 1]?.level || 50}%
                      </Text>
                      <Slider
                        min={0}
                        max={100}
                        value={techStack[techStack.length - 1]?.level || 50}
                        onChange={(value) => {
                          if (techStack.length > 0) {
                            handleTechChange(techStack.length - 1, 'level', value);
                          }
                        }}
                        style={{ flex: 1 }}
                      />
                    </div>
                  </Col>
                  <Col span={4}>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleAddTech}
                      style={{ width: '100%' }}
                    >
                      添加
                    </Button>
                  </Col>
                </Row>
              </Card>
            </Space>
          </Card>

          {/* 技能标签部分 */}
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>技能标签</span>
                <Button
                  type="dashed"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={handleAddSkill}
                  disabled={!tempSkill.trim()}
                >
                  添加技能
                </Button>
              </div>
            }
            style={{ marginBottom: 24 }}
            size="small"
            // 修复: 使用 styles 替代 bodyStyle
            styles={{ body: { padding: 16 } }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <Input
                  placeholder="输入技能标签"
                  value={tempSkill}
                  onChange={(e) => setTempSkill(e.target.value)}
                  onPressEnter={handleAddSkill}
                  style={{ flex: 1 }}
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddSkill}
                  disabled={!tempSkill.trim()}
                >
                  添加
                </Button>
              </div>
              
              {skills.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <Text type="secondary" style={{ marginBottom: 8, display: 'block' }}>
                    已添加的技能：
                  </Text>
                  <Space wrap>
                    {skills.map((skill, index) => (
                      <Tag
                        key={index}
                        closable
                        onClose={() => handleRemoveSkill(skill)}
                        style={{ 
                          padding: '4px 8px',
                          borderRadius: 4,
                          fontSize: 14
                        }}
                      >
                        {skill}
                      </Tag>
                    ))}
                  </Space>
                </div>
              )}
            </Space>
          </Card>

          {/* 操作按钮 */}
          <Card size="small">
            <Row justify="end" gutter={[8, 8]}>
              <Col>
                <Button 
                  onClick={handleCancel}
                >
                  取消
                </Button>
              </Col>
              <Col>
                <Button onClick={handleReset} icon={<UndoOutlined />}>
                  重置
                </Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  onClick={handleSave}
                  loading={loading}
                  icon={<SaveOutlined />}
                >
                  保存更改
                </Button>
              </Col>
            </Row>
          </Card>
        </Form>
      </div>
    </Modal>
  );
};

export default EditProfile;